import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { buildClasses } from "../utils/buildClasses";
import { createContext, useRef, useEffect, useState } from "react";
import { remeasureAfterTransitions } from "./remeasureAfterTransitions";

// Allows children to hook into the container that is actually used for scrolling,
// so they can add scroll listeners, get the height of the container, etc.
export const ScrollContainerContext = createContext<HTMLElement | null>(null);

// Allows children to access the OverlayScrollbars instance for advanced operations
export const OverlayScrollbarsInstanceContext = createContext<any>(null);

export function RyeMainContentContainerBase({
  children,
  roundedTl = true,
  roundedTr = true,
  roundedBr = true,
  roundedBl = true,
  flexGrow = true,
  flexShrink = true,
  height = "full",
  padTop = false,
  padBottom = true,
  overflowY = "auto",
  zIndex = "10",
  fixedHeader,
}: {
  children: React.ReactNode;
  flexGrow?: boolean;
  flexShrink?: boolean;
  height?: "full" | "auto";
  roundedTl?: boolean;
  roundedTr?: boolean;
  roundedBr?: boolean;
  roundedBl?: boolean;
  padTop?: boolean;
  padBottom?: boolean;
  overflowY?: "auto" | "visible";
  zIndex?: "10" | "20";
  /** Content outside the scroll viewport; card callers use the standardized header. */
  fixedHeader?: React.ReactNode;
}): JSX.Element {
  const hasFixedHeader = fixedHeader != null;
  const overlayScrollbarsRef = useRef<any>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [osInstance, setOsInstance] = useState<any>(null);

  useEffect(() => {
    if (overlayScrollbarsRef.current) {
      // Get the viewport element from OverlayScrollbars (see https://www.npmjs.com/package/overlayscrollbars-react#ref)
      const instance = overlayScrollbarsRef.current.osInstance();
      if (instance) {
        const viewport = instance.elements().viewport;
        setScrollElement(viewport);
        setOsInstance(instance);
      }
    }
  }, [hasFixedHeader]);

  useEffect(() => {
    if (!osInstance) return;
    return remeasureAfterTransitions(osInstance);
  }, [osInstance]);

  const scrollContent = (
    <ScrollContainerContext.Provider value={scrollElement}>
      <OverlayScrollbarsInstanceContext.Provider value={osInstance}>
        <OverlayScrollbarsComponent
          ref={overlayScrollbarsRef}
          options={{
            scrollbars: { autoHide: "move" },
            overflow:
              overflowY === "auto"
                ? { y: "scroll" }
                : // Both axes, since a scrolling x axis clips y as well, which
                  // cut off what spills out of top bars, like the search
                  // bar's shadow. The host's overflow is freed below too.
                  { x: "visible", y: "visible" },
          }}
          className={buildClasses(
            {
              switch: zIndex, // OverlayScrollbarsComponent creates a new stacking context, so we need to resolve stacking context ordering with z-index
              cases: new Map([
                ["10", "z-10"],
                ["20", "z-20"],
              ]),
            },
            { if: padTop, then: "pt-[18px]" },
            {
              if: padBottom,
              then: "pb-[18px]",
            },
            // OverlayScrollbars' stylesheet hides the host's overflow.
            { if: overflowY === "visible", then: "!overflow-visible" },
            "h-full",
            STANDARD_MAIN_CONTENT_CARD_PADDING_X,
          )}
        >
          {children}
        </OverlayScrollbarsComponent>
      </OverlayScrollbarsInstanceContext.Provider>
    </ScrollContainerContext.Provider>
  );

  return (
    <div
      className={buildClasses(
        { if: roundedTl, then: "rounded-tl-xl", else: "rounded-tl-sm" },
        { if: roundedTr, then: "rounded-tr-xl", else: "rounded-tr-sm" },
        { if: roundedBr, then: "rounded-br-xl", else: "rounded-br-sm" },
        { if: roundedBl, then: "rounded-bl-xl", else: "rounded-bl-sm" },
        { if: flexGrow, then: "flex-grow", else: "flex-grow-0" },
        { if: flexShrink, then: "flex-shrink", else: "flex-shrink-0" },
        {
          if: hasFixedHeader,
          then: buildClasses("flex", "flex-col", "min-h-0", "overflow-hidden"),
          else: overflowY === "auto" ? "overflow-y-auto" : "overflow-y-visible",
        },
        {
          switch: height,
          cases: new Map([
            ["full", "h-full"],
            ["auto", "h-auto"],
          ]),
        },
        "relative",
      )}
    >
      {hasFixedHeader ? (
        <>
          <div className={buildClasses("shrink-0")}>{fixedHeader}</div>
          <div className={buildClasses("flex-1", "min-h-0")}>
            {scrollContent}
          </div>
        </>
      ) : (
        scrollContent
      )}
    </div>
  );
}

// Shared optical inset for top bars, content, footers, and supporting panes.
export const STANDARD_MAIN_CONTENT_CARD_PADDING_X = "px-[18px]";
